import {
  GestureType,
  PointerType,
  type GestureEvent,
  type GestureConfig,
  type Point2D,
  DEFAULT_GESTURE_CONFIG,
} from "./types";

/**
 * Tracked pointer state during gesture recognition.
 */
interface PointerState {
  id: number;
  startPosition: Point2D;
  currentPosition: Point2D;
  previousPosition: Point2D;
  startTime: number;
  pointerType: PointerType;
  button: number;
  originalEvent: PointerEvent;
}

/**
 * GestureRecognizer - Recognizes gestures from raw pointer events.
 *
 * Supports: tap, double-tap, long-press, drag, pinch, two-finger-pan.
 * Emits GestureEvent objects to registered handlers.
 */
export class GestureRecognizer {
  private canvas: HTMLCanvasElement;
  private config: GestureConfig;

  // Active pointers
  private pointers = new Map<number, PointerState>();

  // Tap detection
  private lastTapTime = 0;
  private lastTapPosition: Point2D | null = null;

  // Long press detection
  private longPressTimer: number | null = null;

  // Current gesture state
  private currentGesture = GestureType.None;
  private isDragging = false;

  // Event handlers
  private onGestureStart?: (event: GestureEvent) => void;
  private onGestureMove?: (event: GestureEvent) => void;
  private onGestureEnd?: (event: GestureEvent) => void;
  private onGestureLeave?: (event: GestureEvent) => void;
  private onHover?: (screenPosition: Point2D) => void;

  constructor(canvas: HTMLCanvasElement, config: Partial<GestureConfig> = {}) {
    this.canvas = canvas;
    this.config = { ...DEFAULT_GESTURE_CONFIG, ...config };
  }

  // ============== Public API ==============

  /**
   * Start listening for gestures.
   */
  attach(handlers: {
    onStart?: (event: GestureEvent) => void;
    onMove?: (event: GestureEvent) => void;
    onEnd?: (event: GestureEvent) => void;
    onLeave?: (event: GestureEvent) => void;
    onHover?: (screenPosition: Point2D) => void;
  }): void {
    this.onGestureStart = handlers.onStart;
    this.onGestureMove = handlers.onMove;
    this.onGestureEnd = handlers.onEnd;
    this.onGestureLeave = handlers.onLeave;
    this.onHover = handlers.onHover;

    this.canvas.addEventListener("pointerdown", this.handlePointerDown);
    this.canvas.addEventListener("pointermove", this.handlePointerMove);
    this.canvas.addEventListener("pointerup", this.handlePointerUp);
    this.canvas.addEventListener("pointercancel", this.handlePointerCancel);
    this.canvas.addEventListener("pointerleave", this.handlePointerLeave);
    this.canvas.addEventListener("pointerenter", this.handlePointerEnter);
  }

  /**
   * Stop listening for gestures.
   */
  detach(): void {
    this.canvas.removeEventListener("pointerdown", this.handlePointerDown);
    this.canvas.removeEventListener("pointermove", this.handlePointerMove);
    this.canvas.removeEventListener("pointerup", this.handlePointerUp);
    this.canvas.removeEventListener("pointercancel", this.handlePointerCancel);
    this.canvas.removeEventListener("pointerleave", this.handlePointerLeave);
    this.canvas.removeEventListener("pointerenter", this.handlePointerEnter);

    this.clearLongPressTimer();
    this.pointers.clear();
    this.currentGesture = GestureType.None;
  }

  /**
   * Get the number of active pointers.
   */
  get pointerCount(): number {
    return this.pointers.size;
  }

  // ============== Event Handlers ==============

  private handlePointerDown = (ev: PointerEvent): void => {
    const pointerType = this.getPointerType(ev);

    // For mouse, only track left button (0) and middle button (1)
    // Right button (2) and others are ignored
    if (
      pointerType === PointerType.Mouse &&
      ev.button !== 0 &&
      ev.button !== 1
    ) {
      return;
    }

    const position = this.getCanvasPosition(ev);

    const state: PointerState = {
      id: ev.pointerId,
      startPosition: position,
      currentPosition: position,
      previousPosition: position,
      startTime: Date.now(),
      pointerType,
      button: ev.button,
      originalEvent: ev,
    };

    this.pointers.set(ev.pointerId, state);

    if (this.pointers.size === 1) {
      // Single pointer - could be tap, long-press, or drag start
      this.startLongPressTimer(ev);
      this.currentGesture = GestureType.None;
      this.isDragging = false;
    } else if (this.pointers.size === 2) {
      // Two pointers - pinch or two-finger pan
      this.clearLongPressTimer();
      this.currentGesture = GestureType.Pinch;

      const gestureEvent = this.createPinchEvent(ev);
      this.onGestureStart?.(gestureEvent);
    }
  };

  private handlePointerMove = (ev: PointerEvent): void => {
    const state = this.pointers.get(ev.pointerId);
    if (!state) {
      // No tracked pointer - this is a hover event (mouse move without button)
      if (ev.pointerType === "mouse" && ev.buttons === 0 && this.onHover) {
        this.onHover(this.getCanvasPosition(ev));
      }
      return;
    }

    // Safety check: if no buttons are pressed (for mouse), end the gesture properly.
    // This can happen on multi-monitor setups where the browser reports buttons=0
    // during a drag due to display boundary crossing or focus changes.
    if (state.pointerType === PointerType.Mouse && ev.buttons === 0) {
      // Emit gestureEnd if we were dragging so the dragger can clean up properly
      if (this.isDragging && this.pointers.size === 1) {
        const gestureEvent = this.createDragEvent(ev, state);
        this.onGestureEnd?.(gestureEvent);
      }

      this.pointers.delete(ev.pointerId);
      if (this.pointers.size === 0) {
        this.clearLongPressTimer();
        this.currentGesture = GestureType.None;
        this.isDragging = false;
      }
      return;
    }

    const position = this.getCanvasPosition(ev);
    state.previousPosition = state.currentPosition;
    state.currentPosition = position;
    state.originalEvent = ev;

    if (this.pointers.size === 1) {
      // Check if we've moved enough to start dragging
      const distance = this.getDistance(state.startPosition, position);

      if (!this.isDragging && distance >= this.config.dragThreshold) {
        // Start drag
        this.clearLongPressTimer();
        this.isDragging = true;
        this.currentGesture = GestureType.Drag;

        const gestureEvent = this.createDragEvent(ev, state);
        this.onGestureStart?.(gestureEvent);
      } else if (this.isDragging) {
        // Continue drag
        const gestureEvent = this.createDragEvent(ev, state);
        this.onGestureMove?.(gestureEvent);
      }
    } else if (this.pointers.size === 2) {
      // Two-finger gesture (pinch/pan)
      const gestureEvent = this.createPinchEvent(ev);
      this.onGestureMove?.(gestureEvent);
    }
  };

  private handlePointerUp = (ev: PointerEvent): void => {
    const state = this.pointers.get(ev.pointerId);
    if (!state) {
      return;
    }

    this.clearLongPressTimer();

    if (this.pointers.size === 1) {
      // Single pointer release
      if (this.isDragging) {
        // End drag
        const gestureEvent = this.createDragEvent(ev, state);
        this.onGestureEnd?.(gestureEvent);
      } else {
        // Check for tap
        const duration = Date.now() - state.startTime;
        const distance = this.getDistance(
          state.startPosition,
          state.currentPosition,
        );

        if (
          duration <= this.config.tapMaxDuration &&
          distance < this.config.dragThreshold
        ) {
          // It's a tap - check for double-tap
          const now = Date.now();
          const timeSinceLastTap = now - this.lastTapTime;
          const distanceFromLastTap = this.lastTapPosition
            ? this.getDistance(this.lastTapPosition, state.currentPosition)
            : Infinity;

          if (
            timeSinceLastTap <= this.config.doubleTapMaxInterval &&
            distanceFromLastTap < this.config.dragThreshold * 2
          ) {
            // Double tap
            const gestureEvent = this.createTapEvent(
              ev,
              state,
              GestureType.DoubleTap,
            );
            this.onGestureEnd?.(gestureEvent);
            this.lastTapTime = 0;
            this.lastTapPosition = null;
          } else {
            // Single tap
            const gestureEvent = this.createTapEvent(
              ev,
              state,
              GestureType.Tap,
            );
            this.onGestureEnd?.(gestureEvent);
            this.lastTapTime = now;
            this.lastTapPosition = state.currentPosition;
          }
        }
      }
    } else if (this.pointers.size === 2) {
      // End of two-finger gesture
      const gestureEvent = this.createPinchEvent(ev);
      this.onGestureEnd?.(gestureEvent);
    }

    this.pointers.delete(ev.pointerId);

    if (this.pointers.size === 0) {
      this.currentGesture = GestureType.None;
      this.isDragging = false;
    }
  };

  private handlePointerCancel = (ev: PointerEvent): void => {
    this.handlePointerUp(ev);
  };

  private handlePointerLeave = (ev: PointerEvent): void => {
    const state = this.pointers.get(ev.pointerId);
    if (!state) return;

    const isSingleActiveDrag = this.isDragging && this.pointers.size === 1;
    if (!isSingleActiveDrag) {
      // Not an active single-pointer drag (e.g. still within tap threshold,
      // or mid-pinch) — fall back to the normal release path, unchanged.
      this.handlePointerUp(ev);
      return;
    }

    // Active single-pointer drag exiting canvas bounds: emit a dedicated
    // "leave" gesture (distinct from a normal release) so each dragger can
    // decide for itself whether that means commit or cancel.
    this.clearLongPressTimer();
    const gestureEvent = this.createDragEvent(ev, state);
    this.onGestureLeave?.(gestureEvent);
    this.pointers.delete(ev.pointerId);
    if (this.pointers.size === 0) {
      this.currentGesture = GestureType.None;
      this.isDragging = false;
    }
  };

  private handlePointerEnter = (ev: PointerEvent): void => {
    // Clean up any stale state when entering
    // If no buttons are pressed for mouse, ensure we have no tracked state
    if (ev.pointerType === "mouse" && ev.buttons === 0) {
      const state = this.pointers.get(ev.pointerId);
      if (state) {
        // Stale state detected - clean up
        this.pointers.delete(ev.pointerId);
        if (this.pointers.size === 0) {
          this.clearLongPressTimer();
          this.currentGesture = GestureType.None;
          this.isDragging = false;
        }
      }
    }
  };

  // ============== Gesture Event Creation ==============

  private createDragEvent(ev: PointerEvent, state: PointerState): GestureEvent {
    return {
      type: GestureType.Drag,
      pointerType: state.pointerType,
      button: state.button,
      screenPosition: state.currentPosition,
      originalEvent: ev,
      delta: {
        x: state.currentPosition.x - state.previousPosition.x,
        y: state.currentPosition.y - state.previousPosition.y,
      },
      timestamp: Date.now(),
    };
  }

  private createTapEvent(
    ev: PointerEvent,
    state: PointerState,
    type: GestureType,
  ): GestureEvent {
    return {
      type,
      pointerType: state.pointerType,
      button: state.button,
      screenPosition: state.currentPosition,
      originalEvent: ev,
      timestamp: Date.now(),
    };
  }

  private createPinchEvent(ev: PointerEvent): GestureEvent {
    const pointerArray = Array.from(this.pointers.values());
    const p1 = pointerArray[0];
    const p2 = pointerArray[1];

    const center: Point2D = {
      x: (p1.currentPosition.x + p2.currentPosition.x) / 2,
      y: (p1.currentPosition.y + p2.currentPosition.y) / 2,
    };

    const currentDistance = this.getDistance(
      p1.currentPosition,
      p2.currentPosition,
    );
    const previousDistance = this.getDistance(
      p1.previousPosition,
      p2.previousPosition,
    );

    return {
      type: GestureType.Pinch,
      pointerType: PointerType.Touch,
      screenPosition: center,
      originalEvent: ev,
      pinchDistance: currentDistance,
      previousPinchDistance: previousDistance,
      pinchCenter: center,
      timestamp: Date.now(),
    };
  }

  private createLongPressEvent(state: PointerState): GestureEvent {
    return {
      type: GestureType.LongPress,
      pointerType: state.pointerType,
      screenPosition: state.currentPosition,
      originalEvent: state.originalEvent,
      timestamp: Date.now(),
    };
  }

  // ============== Long Press Timer ==============

  private startLongPressTimer(ev: PointerEvent): void {
    this.clearLongPressTimer();

    this.longPressTimer = window.setTimeout(() => {
      const state = this.pointers.get(ev.pointerId);
      if (!state) return;

      // Check we haven't moved too much
      const distance = this.getDistance(
        state.startPosition,
        state.currentPosition,
      );
      if (distance < this.config.dragThreshold) {
        this.currentGesture = GestureType.LongPress;
        const gestureEvent = this.createLongPressEvent(state);
        this.onGestureEnd?.(gestureEvent);
      }
    }, this.config.longPressMinDuration);
  }

  private clearLongPressTimer(): void {
    if (this.longPressTimer !== null) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }
  }

  // ============== Utilities ==============

  private getCanvasPosition(ev: PointerEvent): Point2D {
    const rect = this.canvas.getBoundingClientRect();
    return {
      x: ev.clientX - rect.left,
      y: ev.clientY - rect.top,
    };
  }

  private getPointerType(ev: PointerEvent): PointerType {
    switch (ev.pointerType) {
      case "mouse":
        return PointerType.Mouse;
      case "touch":
        return PointerType.Touch;
      case "pen":
        return PointerType.Pen;
      default:
        return PointerType.Unknown;
    }
  }

  private getDistance(p1: Point2D, p2: Point2D): number {
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    return Math.sqrt(dx * dx + dy * dy);
  }
}
